import {
  createPinArray
} from "./chunk-GDLD2H2I.mjs";

// src/use-pin-input.ts
import * as React from "react";
import { assignRef, useControllableState, useId } from "@clara/hooks";
import { callAllHandlers } from "@clara/utils";
var regex = {
  number: /^[0-9]+$/,
  alphanumeric: /^[a-zA-Z0-9]+$/i
};
function usePinInput(props) {
  const {
    autoFocus = true,
    defaultValue,
    id: idProp,
    length = 4,
    manageFocus = true,
    mask,
    onChange: onChangeProp,
    onComplete,
    oneTimeCode = true,
    placeholder = "\u25CB",
    type = "alphanumeric",
    value: valueProp,
    isDisabled,
    isInvalid
  } = props;
  const uuid = useId();
  const id = idProp != null ? idProp : `pin-input-${uuid}`;
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const [value, setValues] = useControllableState({
    value: valueProp,
    defaultValue: defaultValue != null ? defaultValue : "",
    onChange: onChangeProp
  });
  const inputsRef = React.useRef([]);
  const validate = (code) => {
    const re = type instanceof RegExp ? type : type && type in regex ? regex[type] : null;
    return re == null ? void 0 : re.test(code);
  };
  const focusInputField = (dir, index) => {
    if (!manageFocus)
      return;
    const increment = dir === "next" ? 1 : dir === "prev" ? -1 : 0;
    const newIndex = Math.max(Math.min(index + increment, (length != null ? length : 0) - 1), 0);
    inputsRef.current[newIndex].focus();
  };
  const setFieldValue = (val, index) => {
    const values = [...createPinArray(length != null ? length : 0, value)];
    values[index] = val;
    setValues(values.join(""));
  };
  const getInputProps = React.useCallback(
    (props2, ref) => {
      const { index, ...rest } = props2;
      const onChange = (event) => {
        const inputValue = event.target.value;
        const nextCharOrValue = inputValue.length === 2 ? inputValue.split("")[inputValue.length - 1] : inputValue;
        const isValid = validate(nextCharOrValue);
        if (nextCharOrValue.length < 2) {
          if (isValid) {
            setFieldValue(nextCharOrValue, index);
            focusInputField("next", index);
          } else {
            setFieldValue("", index);
          }
        } else if (isValid) {
          setValues(inputValue);
        }
      };
      const onKeyDown = (event) => {
        const { key, target } = event;
        const value2 = target.value;
        switch (key) {
          case "ArrowLeft":
            event.preventDefault();
            focusInputField("prev", index);
            break;
          case "ArrowRight":
            event.preventDefault();
            focusInputField("next", index);
            break;
          case "Delete":
          case "Backspace":
            event.preventDefault();
            setFieldValue("", index);
            if (key === "Backspace" && value2 === "" && index + 1 === length) {
              focusInputField("prev", index);
            } else if (key === "Backspace" || index + 1 !== length) {
              focusInputField("prev", index);
            }
            break;
          default:
            break;
        }
      };
      const onFocus = (event) => {
        event.target.select();
        setFocusedIndex(index);
      };
      const onBlur = () => setFocusedIndex(-1);
      const onPaste = (event) => {
        event.preventDefault();
        const copyValue = event.clipboardData.getData("Text");
        const isValid = validate(copyValue.trim());
        if (isValid) {
          setValues(copyValue);
        }
      };
      const inputType = type === "number" ? "numeric" : "text";
      return {
        type: mask ? "password" : inputType,
        ...rest,
        id: `${id}-${index}`,
        key: `${id}-${index + 1}`,
        onChange: callAllHandlers(rest.onChange, onChange),
        onKeyDown: callAllHandlers(rest.onKeyDown, onKeyDown),
        onFocus: callAllHandlers(rest.onFocus, onFocus),
        onBlur: callAllHandlers(rest.onBlur, onBlur),
        onPaste: callAllHandlers(rest.onPaste, onPaste),
        inputRef: (node) => {
          if (index === 0)
            assignRef(ref, node);
          inputsRef.current[index] = node;
        },
        autoComplete: oneTimeCode ? "one-time-code" : "off",
        placeholder: focusedIndex === index ? "" : placeholder,
        autoFocus: autoFocus && index === 0,
        error: isInvalid,
        disabled: isDisabled
      };
    },
    [
      id,
      oneTimeCode,
      placeholder,
      mask,
      setFocusedIndex,
      setFieldValue,
      manageFocus,
      inputsRef,
      isDisabled,
      isInvalid
    ]
  );
  React.useEffect(() => {
    if (value.length !== length)
      return;
    onComplete == null ? void 0 : onComplete(value);
  }, [value]);
  return {
    // prop getter
    getInputProps,
    // state
    id,
    value,
    // actions
    setValues
  };
}

export {
  usePinInput
};
